import React, { useState, useEffect, useCallback } from 'react';
import { apiErrorText } from '../lib/apiError';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, RefreshCw, Plug, CheckCircle2, XCircle } from 'lucide-react';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const PLATFORM_API = `${API_URL}/v2/super-admin`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` } });

/**
 * Super-Admin GetMyBoat integration panel for a single organization.
 * White-glove setup: enter the org's dedicated inbox + app password, test it
 * (read-only IMAP probe), enable, run a one-time full historical import, or
 * disconnect. The app password is encrypted server-side and never returned.
 */
export default function GmbIntegrationCard({ org, requirePin }) {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({ imapUser: '', imapPassword: '', imapHost: 'imap.gmail.com', imapPort: 993 });
  const [testResult, setTestResult] = useState(null);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    if (!org?.id) return;
    try {
      const r = await axios.get(`${PLATFORM_API}/organizations/${org.id}/gmb`, authHeaders());
      setStatus(r.data);
      setForm((f) => ({
        ...f,
        imapUser: r.data.imapUser || '',
        imapHost: r.data.imapHost || 'imap.gmail.com',
        imapPort: r.data.imapPort || 993,
        imapPassword: '',
      }));
    } catch (e) {
      // org may simply have no connection yet — leave defaults
    }
  }, [org?.id]);

  useEffect(() => { load(); }, [load]);

  const runTest = async () => {
    setBusy('test'); setTestResult(null);
    try {
      const r = await axios.post(`${PLATFORM_API}/organizations/${org.id}/gmb/test`, form, authHeaders());
      setTestResult(r.data);
      if (r.data.ok) toast.success(`Connected — found ${r.data.gmbEmailCount} GetMyBoat emails`);
      else toast.error(r.data.error || 'Connection failed');
    } catch (e) {
      toast.error(apiErrorText(e, 'Test failed'));
    } finally { setBusy(''); }
  };

  const connect = () => {
    const doConnect = async (pin, otp) => {
      const headers = { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` };
      if (otp) { headers['X-Stepup-Token'] = otp; headers['X-Admin-Otp'] = otp; }
      await axios.post(`${PLATFORM_API}/organizations/${org.id}/gmb/connect`, form, { headers });
      toast.success('GetMyBoat connected & enabled');
      setTestResult(null);
      await load();
    };
    if (requirePin) {
      requirePin(`Connect GetMyBoat for "${org.name}"`, doConnect);
    } else {
      doConnect().catch((e) => toast.error(apiErrorText(e, 'Failed to connect')));
    }
  };

  const fullImport = async () => {
    setBusy('import');
    try {
      const r = await axios.post(`${PLATFORM_API}/organizations/${org.id}/gmb/sync-full`, {}, authHeaders());
      toast.success(r.data.message || 'Full import started');
      setTimeout(load, 4000);
    } catch (e) {
      toast.error(apiErrorText(e, 'Failed to start import'));
    } finally { setBusy(''); }
  };

  const disable = () => {
    const doDisable = async (pin, otp) => {
      const headers = { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` };
      if (otp) { headers['X-Stepup-Token'] = otp; headers['X-Admin-Otp'] = otp; }
      await axios.post(`${PLATFORM_API}/organizations/${org.id}/gmb/disable`, {}, { headers });
      toast.success('GetMyBoat disconnected');
      await load();
    };
    if (requirePin) {
      requirePin(`Disconnect GetMyBoat for "${org.name}" — imported bookings stay, syncing stops`, doDisable);
    } else {
      doDisable().catch(() => toast.error('Failed to disable'));
    }
  };

  const connected = status?.connected;

  return (
    <div className="rounded-xl border border-gray-200 p-4" data-testid="sa-gmb-card">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img src="/images/integrations/getmyboat-logo.png" alt="GetMyBoat" className="h-7 w-7 rounded" />
          <div>
            <div className="font-semibold text-gray-900">GetMyBoat Integration</div>
            <div className="text-xs text-gray-500">Read-only email ingestion · per organization</div>
          </div>
        </div>
        <span className={`inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium ${connected ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
          <span className={`h-2 w-2 rounded-full ${connected ? 'bg-emerald-500' : 'bg-gray-400'}`} />
          {connected ? 'Connected' : 'Not connected'}
        </span>
      </div>

      {status && (status.lastSyncedAt || status.totalImported > 0 || status.lastError) && (
        <div className="mb-3 space-y-0.5 text-xs text-gray-500">
          {status.lastSyncedAt && <div>Last synced: {new Date(status.lastSyncedAt).toLocaleString()}</div>}
          <div>Imported bookings: <span className="font-medium text-gray-700">{status.totalImported || 0}</span></div>
          {status.lastError && <div className="text-rose-600">Last error: {status.lastError}</div>}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <Label className="text-xs">Notification email (inbox that receives GetMyBoat mail)</Label>
          <Input value={form.imapUser} onChange={(e) => setForm({ ...form, imapUser: e.target.value })}
            placeholder="support@theircompany.com" className="mt-1" data-testid="sa-gmb-email" />
        </div>
        <div className="col-span-2">
          <Label className="text-xs">
            App password {status?.hasPassword && <span className="text-gray-400">(leave blank to keep current)</span>}
          </Label>
          <Input type="password" value={form.imapPassword}
            onChange={(e) => setForm({ ...form, imapPassword: e.target.value })}
            placeholder={status?.hasPassword ? '••••••••••••••••' : 'xxxx xxxx xxxx xxxx'}
            className="mt-1 font-mono" data-testid="sa-gmb-password" />
        </div>
        <div>
          <Label className="text-xs">IMAP host</Label>
          <Input value={form.imapHost} onChange={(e) => setForm({ ...form, imapHost: e.target.value })} className="mt-1" />
        </div>
        <div>
          <Label className="text-xs">Port</Label>
          <Input type="number" value={form.imapPort}
            onChange={(e) => setForm({ ...form, imapPort: parseInt(e.target.value, 10) || 993 })} className="mt-1" />
        </div>
      </div>

      {testResult && (
        <div className={`mt-3 flex items-center gap-2 text-sm ${testResult.ok ? 'text-emerald-700' : 'text-rose-600'}`}>
          {testResult.ok ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
          {testResult.ok ? `Works — ${testResult.gmbEmailCount} GetMyBoat emails found` : testResult.error}
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <Button variant="outline" onClick={runTest} disabled={!!busy || !form.imapUser} data-testid="sa-gmb-test">
          {busy === 'test' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plug className="mr-2 h-4 w-4" />} Test connection
        </Button>
        <Button onClick={connect} disabled={!!busy || !form.imapUser} className="bg-blue-600 hover:bg-blue-700" data-testid="sa-gmb-save">
          {busy === 'connect' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save &amp; Enable
        </Button>
        {connected && (
          <Button variant="outline" onClick={fullImport} disabled={!!busy} data-testid="sa-gmb-fullimport">
            {busy === 'import' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />} Run full import
          </Button>
        )}
        {connected && (
          <Button variant="outline" onClick={disable} disabled={!!busy}
            className="border-rose-200 text-rose-600 hover:bg-rose-50" data-testid="sa-gmb-disable">
            Disconnect
          </Button>
        )}
      </div>
    </div>
  );
}
